import { useCallback, type ReactNode, type ReactElement } from "react";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpFromDot } from "lucide-react";
import AnimatedWaiting from "@/components/AnimatedWaiting";

type WithPullToRefreshProps = {
  children: ReactNode;
  queryKey: string[];
  refetch: () => Promise<unknown>;
  className?: string;
};

/**
 * Higher-order component that wraps content with pull-to-refresh functionality
 *
 * @param children - The content to be wrapped
 * @param queryKey - The query key to invalidate when refreshing
 * @param refetch - The refetch function to call when refreshing
 * @param className - Optional additional class names
 */
export function WithPullToRefresh({
  children,
  queryKey,
  refetch,
  className,
}: WithPullToRefreshProps) {
  const queryClient = useQueryClient();

  // Handle pull-to-refresh functionality
  const handleRefresh = useCallback(async () => {
    // Invalidate and refetch queries related to matches
    await queryClient.invalidateQueries({ queryKey });
    await refetch();

    return Promise.resolve(); // Library expects a promise to be returned
  }, [queryClient, refetch, queryKey]);

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <div className="flex justify-center items-center p-2 text-zinc-400">
          <div className="flex flex-col items-center gap-1">
            <ArrowUpFromDot className="size-4 animate-bounce" />
            <p className="text-xs">Pull to refresh</p>
          </div>
        </div>
      }
      refreshingContent={
          <AnimatedWaiting prefix="" className="text-lg mt-4" />
      }
      className={`h-full w-full ${className || ""}`}
      pullDownThreshold={60}
      maxPullDownDistance={120}
      resistance={4}
      backgroundColor="transparent"
    >
      {/* Using JSX.Element[] | JSX.Element to resolve type issue with react-simple-pull-to-refresh */}
      {children as ReactElement}
    </PullToRefresh>
  );
}
